import { SnackToast as Toast } from "@/utils/snackToast";
import { General } from "@/utils/constants";
import { parseJwt } from "@/utils/transformers";
import debounce from "lodash/debounce";
import { checkCPF } from "@/utils/validate";
import {
  USER_PERMISSIONS as userPermissions,
  SIZES as sizes
} from "@/utils/enumerate";

import router from "@/router";
import {
  userLogin,
  userLoginGuardian,
  getUserData,
  userLogout,
  createUserDefault,
  getUserByFilter,
  getUserByFilterAdm,
  getUsersByFilterAdm,
  getUsersByFilterProfiles,
  getUsersGenericByFilter,
  setUserProfiles,
  updateUserProfiles,
  setProfileActive,
  deleteUserProfiles,
  getDefaultCapacity,
  getBookmarks,
  getBookmarksById,
  getSignatureBookmarks,
  getProcessBookmarks,
  saveBookmark,
  editBookmark,
  deleteBookmark,
  getAdmBookmarks,
  getAdmBookmarksById,
  saveAdmBookmark,
  editAdmBookmark,
  getNotificationConfig,
  getUserNotificationConfig,
  setUserNotificationConfig,
  getNotificationsList,
  setNotificationReaded,
  setAllNotificationReaded,
  deleteNotification,
  userTokenLoginDigitalCert,
  userLoginDigitalCert,
  listMessagesAdm,
  createMessage,
  deleteMessage,
  listMessages,
  readMessage,
  readAllMessage,
  getBookmarksUser,
  disableNotificationPublic,
  getBookmarkUse,
  deleteBookmarkAdm,
  deleteBookmarkAdmBatch
} from "@/services/api/ApiUser";

import {
  IUserFilter,
  ResponseGenericType,
  ResponsePaged,
  ResponseType
} from "@/store/types/types";
import { ActionContext } from "vuex/types";
import {
  ILoginRequest,
  INotificationType,
  IToken,
  IUser,
  IUserProfile,
  IUserRequest,
  INotificationConfig,
  IUserNotification,
  ITokenGuardian,
  IUserConfig,
  IUserNotificationFilter,
  IUserNotificationFilterOnlyCount,
  IBond,
  IUserMessageRequest,
  IListMessage,
  IUserMessageOnlyCount,
  INotificationProcessInfo
} from "@/store/types/user-types";
import {
  IBookmark,
  IBookmarkPaged,
  IBookmarkParam,
  IBookmarkRequest,
  IBookmarkUsed,
  ISignatureBookmarks
} from "@/store/types/bookmark-types";
import { IProfile } from "@/store/types/control-types";
import { showErrorMessage, showSuccessMessage } from "@/utils/messageUtility";
import { RootState } from "@/store/types/root";
import { isDocumentType } from "@/utils/notifications";

const getDefaultState = {
  currentUser: {} as IUser,
  editableUser: {} as IUser,
  token: null as IToken | null,
  bookmarks: [] as IBookmark[],
  bookmarksUser: [] as IBookmark[],
  bookmarkAdm: {} as ResponsePaged<IBookmark>,
  usersSearch: [] as IUser[],
  loginDate: 0,
  notificationConfig: [] as INotificationType[],
  userNotificationConfig: [] as INotificationConfig[],
  paginatedNotificationsResults: [] as IUserNotification[],
  paginatedNotificationsCount: 0,
  recentNotificationsResults: [] as IUserNotification[],
  recentNotificationsCount: 0,
  unreadedRecentNotificationsResults: [] as IUserNotification[],
  unreadedRecentNotificationsCount: 0,
  tokenDigitalCert: "",
  userConfig: { theme: "light", fontSize: "normal" },
  errorMessage: "" as string | string[],
  signatureBookmarks: {} as ISignatureBookmarks,
  filledKeySignBookmark: "" as string,
  processBookmarks: [] as IBookmark[],
  userSearchFilters: {},
  userSearchManager: {} as ResponsePaged<IUser>,
  screenWidth: null as number | null,
  sidebarOpenned: true,
  listMessageAdm: {} as ResponsePaged<IListMessage>,
  listMessage: [] as IListMessage[],
  unreadMessageCount: 0
};

const state = getDefaultState;

type State = typeof state;

const mutations = {
  loginUser: (
    state: State,
    { token, userData }: { token: IToken; userData: IUser }
  ) => {
    try {
      if (token) {
        state.token = token;
        state.loginDate = parseJwt(state.token).date;
      }
      if (userData) state.currentUser = userData;
    } catch (error) {
      console.error(error);
      localStorage.removeItem("token");
      Toast.open({
        message: General.TOKEN_FAILURE,
        type: "is-danger",
        position: "is-top",
        duration: 8000,
        pauseOnHover: true,
        queue: true
      });
    }
  },
  invalidToken: (state: State) => {
    if (state.token) state.token = null;
  },

  setEditableUser: (state: State, user: IUser) => {
    state.editableUser = user;
  },

  bookmarkAdmSuccess: (state: State, data: ResponsePaged<IBookmark>) => {
    state.bookmarkAdm = data;
  },

  bookmarkSuccess: (state: State, data: IBookmark[]) => {
    state.bookmarks = data;
  },

  bookmarkUserSuccess: (state: State, data: IBookmark[]) => {
    state.bookmarksUser = data;
  },

  saveBookmarkSuccess: (state: State, data: IBookmark) => {
    Array.isArray(state.bookmarks) && state.bookmarks.push(data);
  },

  signatureBookmarkSuccess: (state: State, data: ISignatureBookmarks) => {
    state.signatureBookmarks = data;
    state.filledKeySignBookmark = Object.keys(data)[0];
  },

  setUsersFromSearch: (state: State, data: IUser[]) => {
    state.usersSearch = data;
  },

  setNotificationConfig: (state: State, data: INotificationType[]) => {
    state.notificationConfig = data;
  },

  setUserNotificationConfig: (state: State, data: INotificationConfig[]) => {
    state.userNotificationConfig = data;
  },

  setPaginatedNotifications: (
    state: State,
    data: ResponsePaged<IUserNotification>
  ) => {
    state.paginatedNotificationsResults = data.results;
    state.paginatedNotificationsCount = data.count;
  },

  setRecentNotifications: (
    state: State,
    data: ResponsePaged<IUserNotification>
  ) => {
    state.recentNotificationsResults = data.results;
    state.recentNotificationsCount = data.count;
  },

  setUnreadedRecentNotifications: (
    state: State,
    data: ResponsePaged<IUserNotification>
  ) => {
    state.unreadedRecentNotificationsResults = data.results;
    state.unreadedRecentNotificationsCount = data.count;
  },

  setUnreadedOnlyCountNotifications: (
    state: State,
    data: IUserNotificationFilterOnlyCount
  ) => {
    state.unreadedRecentNotificationsCount = data.count;
  },

  setReadPaginatedNotifications: (state: State, data: IUserNotification[]) => {
    data.forEach(readedNotification => {
      const indexNotificationToUpdate = state.paginatedNotificationsResults.findIndex(
        paginatedNotification =>
          paginatedNotification.id == readedNotification.id
      );

      if (indexNotificationToUpdate > -1) {
        state.paginatedNotificationsResults[indexNotificationToUpdate] = {
          ...readedNotification
        };
      }
    });
  },

  setReadRecentNotifications: (state: State, data: IUserNotification[]) => {
    data.forEach(readedNotification => {
      const indexNotificationToUpdate = state.recentNotificationsResults.findIndex(
        recentNotification => recentNotification.id == readedNotification.id
      );
      if (indexNotificationToUpdate > -1) {
        state.recentNotificationsResults[indexNotificationToUpdate] = {
          ...readedNotification
        };
      }
    });
  },

  setReadUnreadedRecentNotifications: (
    state: State,
    data: IUserNotification[]
  ) => {
    data.forEach(readedNotification => {
      const indexNotificationToUpdate = state.unreadedRecentNotificationsResults.findIndex(
        unreadedNotification => unreadedNotification.id == readedNotification.id
      );
      if (indexNotificationToUpdate > -1) {
        state.unreadedRecentNotificationsResults.splice(
          indexNotificationToUpdate,
          1
        );
        state.unreadedRecentNotificationsCount--;
      }
    });
  },

  setTokenDigitalCert: (state: State, data: ITokenGuardian) => {
    state.tokenDigitalCert = data.hash;
  },

  socketNotification: (state: State, data: IUserNotification) => {
    if (data.type === "Removed Notification") {
      let indexDeletedNotification = state.paginatedNotificationsResults.findIndex(
        notification => notification.id === data.id
      );
      if (indexDeletedNotification >= 0) {
        state.paginatedNotificationsResults.splice(indexDeletedNotification, 1);
        state.paginatedNotificationsCount--;
      }

      indexDeletedNotification = state.recentNotificationsResults.findIndex(
        notification => notification.id === data.id
      );
      if (indexDeletedNotification >= 0) {
        state.recentNotificationsResults.splice(indexDeletedNotification, 1);
        state.recentNotificationsCount--;
      }

      indexDeletedNotification = state.unreadedRecentNotificationsResults.findIndex(
        notification => notification.id === data.id
      );
      if (indexDeletedNotification >= 0) {
        state.unreadedRecentNotificationsResults.splice(
          indexDeletedNotification,
          1
        );
        state.unreadedRecentNotificationsCount--;
      }
    } else {
      state.paginatedNotificationsResults.unshift(data);
      state.paginatedNotificationsCount++;
      state.recentNotificationsResults.unshift(data);
      state.recentNotificationsCount++;
      state.unreadedRecentNotificationsResults.unshift(data);
      state.unreadedRecentNotificationsCount++;

      if (state.paginatedNotificationsResults.length > 10)
        state.paginatedNotificationsResults.pop();

      if (state.recentNotificationsResults.length > 20)
        state.recentNotificationsResults.pop();

      if (state.unreadedRecentNotificationsResults.length > 20)
        state.unreadedRecentNotificationsResults.pop();
    }
  },

  setConfig: (state: State, config: IUserConfig) => {
    state.userConfig = config;
    localStorage.setItem("PERSON_CONFIG", JSON.stringify(config));
    document.documentElement.setAttribute("theme", config.theme);
    document.documentElement.setAttribute("fontSize", config.fontSize);
  },

  currentUserLogout: (state: State, message: string | null) => {
    localStorage.removeItem("token");
    window.sessionStorage.clear();
    Object.assign(state, getDefaultState);
    router.push("/");
    if (router.currentRoute.path !== "/") location.replace("/");

    if (message)
      Toast.open({
        message,
        type: "is-info",
        duration: 8000
      });
    else
      Toast.open({
        message: "Sua sessão expirou!",
        type: "is-danger",
        duration: 8000,
        queue: true
      });
  },

  processBookmarkSuccess: (state: State, data: IBookmark[]) => {
    state.processBookmarks = data;
  },

  setUserSearchFilters: (state: State, data: IUserFilter) => {
    state.userSearchFilters = data;
  },

  setUserSearchManager: (state: State, data: ResponsePaged<IUser>) => {
    state.userSearchManager = data;
  },

  setScreen: (state: State, data: number) => {
    state.screenWidth = data;
  },

  setSidebar: (state: State, data: boolean) => {
    state.sidebarOpenned = data;
  },

  toggleTheme: (state: State) => {
    state.userConfig.theme =
      state.userConfig.theme === "light" ? "dark" : "light";
    localStorage.setItem("PERSON_CONFIG", JSON.stringify(state.userConfig));
    document.documentElement.setAttribute("theme", state.userConfig.theme);
  },

  setListMessageAdm: (state: State, data: ResponsePaged<IListMessage>) => {
    state.listMessageAdm = data;
  },

  setListMessage: (state: State, data: ResponsePaged<IListMessage>) => {
    state.listMessage = data.results;
  },

  setUnreadMessageCount: (state: State, data: number) => {
    state.unreadMessageCount = data;
  },

  updateMessageList: (state: State, messageId: string) => {
    state.listMessage.forEach(message => {
      if (message.id === messageId) message.is_readed = true;
      return { ...message };
    });
    state.unreadMessageCount--;
  },

  updateMessageWebSocket: (state: State, data: IListMessage) => {
    if (data) state.unreadMessageCount++;
  }
};

const actions = {
  authLogin: async (
    { commit }: ActionContext<State, RootState>,
    user: ILoginRequest
  ) => {
    const resultsUserToken: ResponseType<IToken> = await userLogin(user);

    if (resultsUserToken.kind === "ok") {
      const resultsUserData: ResponseType<IUser> = await getUserData();

      if (resultsUserData.kind === "ok") {
        commit("loginUser", {
          token: resultsUserToken.data,
          userData: resultsUserData.data
        });
        if (resultsUserToken.message)
          showSuccessMessage({ message: resultsUserToken.message });
      } else showErrorMessage(resultsUserData.message);
    } else
      showErrorMessage(
        resultsUserToken.message,
        undefined,
        resultsUserToken.kind === "Forbidden" ? "is-danger" : undefined
      );
  },

  authLoginGuardian: async (
    { commit }: ActionContext<State, RootState>,
    token: string
  ) => {
    const resultsUserToken: ResponseType<IToken> = await userLoginGuardian(
      token
    );

    if (resultsUserToken.kind === "ok") {
      const resultsUserData: ResponseType<IUser> = await getUserData();

      if (resultsUserData.kind === "ok") {
        commit("loginUser", {
          token: resultsUserToken.data,
          userData: resultsUserData.data
        });
        if (resultsUserToken.message) {
          showSuccessMessage({ message: resultsUserToken.message });
        }
        return true;
      } else showErrorMessage(resultsUserData.message);
    } else showErrorMessage(resultsUserToken.message);
  },

  userDataRequest: async (_: ActionContext<State, RootState>) => {
    const results: ResponseType<IUser> = await getUserData();

    if (results.kind === "ok") return results.data;
    showErrorMessage(results.message);
  },

  authLogout: async (
    { commit }: ActionContext<State, RootState>,
    message: string | null
  ) => {
    commit("currentUserLogout", message);
    await userLogout();
  },

  updateDefaultProfile: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    capacityId: number
  ) => {
    const resultsUserToken: ResponseType<IToken> = await createUserDefault(
      capacityId,
      false
    );

    if (resultsUserToken.kind === "ok") {
      const resultsUserData: ResponseType<IUser> = await getUserData(false);

      if (resultsUserData.kind === "ok") {
        commit("setViewProcessesToStandard");
        commit("loginUser", {
          token: resultsUserToken.data,
          userData: resultsUserData.data
        });
        dispatch("sendAllChannels", {
          token: resultsUserToken.data,
          userData: resultsUserData.data,
          service: "changeProfile"
        });
        if (resultsUserToken.message)
          showSuccessMessage({ message: resultsUserToken.message });
        return true;
      } else showErrorMessage(resultsUserData.message);
    } else showErrorMessage(resultsUserToken.message);
    return false;
  },

  bookmarkRequest: async (
    { commit }: ActionContext<State, RootState>,
    { name, all = true }: { name: string; all: boolean }
  ) => {
    const results: ResponseType<IBookmark[]> = await getBookmarks(name);

    if (results.kind === "ok" && results.data) {
      const data = all
        ? results.data
        : results.data?.filter(bookmark => !bookmark.in_person_bidding);
      commit("bookmarkSuccess", data);
    }
  },

  bookmarkUserRequest: async (
    { commit }: ActionContext<State, RootState>,
    params: IBookmarkParam
  ) => {
    const results: ResponseType<IBookmark[]> = await getBookmarksUser(params);

    if (results.kind === "ok") commit("bookmarkUserSuccess", results.data);
  },

  signatureBookmarkRequest: async (
    { commit }: ActionContext<State, RootState>,
    bookmarkName = ""
  ) => {
    const results: ResponseType<ISignatureBookmarks> = await getSignatureBookmarks(
      bookmarkName
    );

    if (results.kind === "ok") commit("signatureBookmarkSuccess", results.data);
  },

  bookmarkByIdRequest: async (
    _: ActionContext<State, RootState>,
    bookmarkId: string
  ) => {
    const results: ResponseType<IBookmark> = await getBookmarksById(bookmarkId);

    if (results.kind !== "ok") showErrorMessage(results.message);

    return results.data;
  },

  getProcessBookmarkRequest: debounce(async function(
    { commit }: ActionContext<State, RootState>,
    { capacityId, tab, capacity, with_children }: IBookmarkRequest
  ) {
    const results: ResponseType<IBookmark[]> = await getProcessBookmarks({
      capacityId,
      tab,
      capacity,
      with_children
    });

    if (results.kind === "ok") commit("processBookmarkSuccess", results.data);
    else showErrorMessage(results.message);
  },
  500),

  saveBookmarkRequest: async (
    { commit }: ActionContext<State, RootState>,
    bookmark: IBookmark
  ) => {
    const results: ResponseType<IBookmark> = await saveBookmark(bookmark);

    if (results.kind === "ok") commit("saveBookmarkSuccess", results.data);
    else showErrorMessage(results.message);

    return results;
  },

  editBookmark: async (
    _: ActionContext<State, RootState>,
    bookmark: IBookmark
  ) => {
    const results: ResponseType<IBookmark[]> = await editBookmark(bookmark);
    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  deleteBookmark: async (
    _: ActionContext<State, RootState>,
    bookmarkId: string
  ) => {
    const results: ResponseType<IBookmark[]> = await deleteBookmark(bookmarkId);

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  admBookmarksRequest: async (
    { commit }: ActionContext<State, RootState>,
    config: IBookmarkPaged
  ) => {
    const results: ResponseType<ResponsePaged<
      IBookmark
    >> = await getAdmBookmarks(config);

    if (results.kind === "ok") commit("bookmarkAdmSuccess", results.data);
  },

  admBookmarkByIdRequest: async (
    _: ActionContext<State, RootState>,
    bookmarkId: number
  ) => {
    const results: ResponseType<IBookmark> = await getAdmBookmarksById(
      bookmarkId
    );

    if (results.kind !== "ok") showErrorMessage(results.message);

    return results.data;
  },

  saveAdmBookmark: async (
    _: ActionContext<State, RootState>,
    bookmark: IBookmark
  ) => {
    const results: ResponseGenericType = await saveAdmBookmark(bookmark);

    if (results.kind !== "ok") showErrorMessage(results.message);

    return results.kind === "ok";
  },

  editAdmBookmark: async (
    _: ActionContext<State, RootState>,
    bookmark: IBookmark
  ) => {
    const results: ResponseType<IBookmark[]> = await editAdmBookmark(bookmark);
    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  getBookmarkUse: async (
    _: ActionContext<State, RootState>,
    bookmarkId: string
  ) => {
    const results: ResponseType<IBookmarkUsed> = await getBookmarkUse(
      bookmarkId
    );

    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.data;
  },

  deleteBookmarkAdm: async (
    _: ActionContext<State, RootState>,
    bookmarkId: string
  ) => {
    const results: ResponseGenericType = await deleteBookmarkAdm(bookmarkId);

    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  deleteBookmarkAdmBatch: async (
    _: ActionContext<State, RootState>,
    bookmarkIdList: string[]
  ) => {
    const results: ResponseGenericType = await deleteBookmarkAdmBatch(
      bookmarkIdList
    );

    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  userFilterRequest: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserRequest
  ) => {
    if (filter.cpf && !checkCPF(filter.cpf)) {
      Toast.open({
        message: General.FAIL_SEARCH_CPF,
        type: "is-warning",
        duration: 8000
      });
      commit("setUsersFromSearch", []);
      return;
    }
    const results: ResponseType<IUser[]> = await getUserByFilter(filter);
    if (results.kind === "ok" && results.data) {
      results.data.length === 1 && commit("setEditableUser", results.data[0]);
      commit("setUsersFromSearch", results.data);
      if (results.data.length === 0 && filter.cpf)
        Toast.open({
          message: General.FAIL_SEARCH_CPF,
          type: "is-warning",
          duration: 8000
        });
    } else showErrorMessage(results.message);
  },

  userFilterAdmRequest: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserRequest
  ) => {
    const results: ResponseType<IUser[]> = await getUserByFilterAdm(filter);
    const status = results.kind === "ok";
    if (status && results.data) {
      commit("setUsersFromSearch", results.data);
      if (results.data.length === 1) commit("setEditableUser", results.data[0]);
      else if (results.data.length === 0) return router.back();
    } else showErrorMessage(results.message);

    return status;
  },

  userListFilterAdmRequest: async (
    { commit, getters }: ActionContext<State, RootState>,
    filter: IUserRequest
  ) => {
    const results: ResponseType<ResponsePaged<
      IUser
    >> = await getUsersByFilterAdm(filter);

    if (results.kind === "ok") {
      commit("setUserSearchManager", {
        ...results.data,
        results: results.data?.results?.map(user => ({
          ...user,
          bonds: user.bonds.map((bound: IBond) => ({
            ...bound,
            ...getters.getCapacityById(bound.capacity_id)
          }))
        }))
      });
    } else showErrorMessage(results.message);
  },

  userListFilterProfileRequest: async (
    { commit, getters }: ActionContext<State, RootState>,
    filter: IUserRequest
  ) => {
    const results: ResponseType<ResponsePaged<
      IUser
    >> = await getUsersByFilterProfiles(filter);

    if (results.kind === "ok") {
      commit("setUserSearchManager", {
        ...results.data,
        results: results.data?.results?.map(user => ({
          ...user,
          bonds: user.bonds.map((bound: IBond) => ({
            ...bound,
            ...getters.getCapacityById(bound.capacity_id)
          }))
        }))
      });
    } else showErrorMessage(results.message);
  },

  userGenericFilterRequest: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserRequest
  ) => {
    commit("setUsersFromSearch", []);
    const results: ResponseType<IUser[]> = await getUsersGenericByFilter(
      filter
    );

    if (results.kind === "ok" && results.data) {
      results.data.length === 1 && commit("setEditableUser", results.data[0]);
      commit("setUsersFromSearch", results.data);
    } else showErrorMessage(results.message);
  },

  addUserProfile: async (
    _: ActionContext<State, RootState>,
    { user, userProfile }: { user: IUser; userProfile: IUserProfile }
  ) => {
    const results: ResponseGenericType = await setUserProfiles(
      user,
      userProfile
    );

    if (results.kind === "ok")
      showSuccessMessage({
        message: General.MESSAGES.CAPACITY_SUCCESSFULLY_SAVED
      });
    else showErrorMessage(results.message);
  },

  updateUserProfile: async (
    _: ActionContext<State, RootState>,
    {
      user,
      userProfileId,
      userProfile
    }: { user: IUser; userProfileId: string; userProfile: IUserProfile }
  ) => {
    const results: ResponseGenericType = await updateUserProfiles(
      user,
      userProfileId,
      userProfile
    );

    if (results.kind === "ok")
      showSuccessMessage({
        message: General.MESSAGES.CAPACITY_SUCCESSFULLY_UPDATED
      });
    else showErrorMessage(results.message);
  },

  activeProfile: async (
    { commit }: ActionContext<State, RootState>,
    {
      userProfileId,
      active,
      me = false
    }: { userProfileId: string; active: boolean; me: boolean }
  ) => {
    const results: ResponseGenericType = await setProfileActive(
      userProfileId,
      active
    );
    if (results.kind === "ok" && me) {
      const resultsUserData: ResponseType<IUser> = await getUserData();
      if (resultsUserData.kind === "ok") {
        commit("loginUser", {
          userData: resultsUserData.data
        });
      } else {
        showErrorMessage(resultsUserData.message);
      }
    }
    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  deleteUserProfile: async (
    _: ActionContext<State, RootState>,
    { userId, userProfile }: { userId: number; userProfile: IUserProfile }
  ) => {
    const results: ResponseGenericType = await deleteUserProfiles(
      userId,
      userProfile
    );

    if (results.kind === "ok")
      showSuccessMessage({
        message: General.MESSAGES.CAPACITY_SUCCESSFULLY_DELETED
      });
    else showErrorMessage(results.message);
  },

  getDefaultByCapacity: async (
    _: ActionContext<State, RootState>,
    capacityId: string
  ) => {
    const results: ResponseType<IProfile> = await getDefaultCapacity(
      capacityId
    );

    if (results.kind !== "ok") showErrorMessage(results.message);
    return results;
  },

  requestNotificationConfig: async ({
    commit,
    getters
  }: ActionContext<State, RootState>) => {
    const now = new Date().toLocaleDateString("pt-BR");

    if (getters.getNotificationConfigCache.date === now) {
      commit("setNotificationConfig", getters.getNotificationConfigCache.data);
      return;
    }

    const results: ResponseType<INotificationType[]> = await getNotificationConfig();

    if (results.kind === "ok") {
      commit("setNotificationConfig", results.data);
      commit("setNotificationConfigCache", { date: now, data: results.data });
    } else showErrorMessage(results.message);
  },

  requestUserNotificationConfig: async ({
    commit
  }: ActionContext<State, RootState>) => {
    const results: ResponseType<INotificationConfig[]> = await getUserNotificationConfig();

    if (results.kind === "ok")
      commit("setUserNotificationConfig", results.data);
    else showErrorMessage(results.message);
  },

  saveUserNotificationConfig: async (
    _: ActionContext<State, RootState>,
    userNotificationConfig: INotificationConfig[]
  ) => {
    const results: ResponseGenericType = await setUserNotificationConfig(
      userNotificationConfig
    );
    const status = results.kind === "ok";

    if (!status) showErrorMessage(results.message);

    return status;
  },

  userNotificationRequest: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserNotificationFilter = {
      currentPage: 1,
      perPage: 20,
      listType: "",
      search: ""
    }
  ) => {
    let results: ResponseType<ResponsePaged<IUserNotification>> = { kind: "" };

    if (filter.listType == "USER_AREA")
      results = await getNotificationsList({ ...filter, isReaded: undefined });
    if (filter.listType == "BELL_AREA_ALL")
      results = await getNotificationsList({ ...filter, isReaded: undefined });
    if (filter.listType == "BELL_AREA_UNREAD")
      results = await getNotificationsList({ ...filter, isReaded: false });

    if (results?.kind === "ok") {
      if (filter.listType == "USER_AREA")
        commit("setPaginatedNotifications", results.data);
      if (filter.listType == "BELL_AREA_ALL")
        commit("setRecentNotifications", results.data);
      if (filter.listType == "BELL_AREA_UNREAD")
        commit("setUnreadedRecentNotifications", results.data);
    } else showErrorMessage(results.message);
  },

  userNotificationOnlyCountRequest: async ({
    commit
  }: ActionContext<State, RootState>) => {
    const results: ResponseType<IUserNotificationFilterOnlyCount> = await getNotificationsList(
      {
        currentPage: 1,
        perPage: 20,
        only_count: true,
        isReaded: false
      }
    );

    if (results?.kind === "ok")
      commit("setUnreadedOnlyCountNotifications", results.data);
    else showErrorMessage(results.message);
  },

  everyTypeUserNotificationRequest: async ({
    dispatch
  }: ActionContext<State, RootState>) => {
    dispatch("userNotificationRequest", {
      currentPage: 1,
      perPage: 10,
      listType: "USER_AREA"
    });
    dispatch("userNotificationRequest", {
      currentPage: 1,
      perPage: 20,
      listType: "BELL_AREA_ALL"
    });
    dispatch("userNotificationRequest", {
      currentPage: 1,
      perPage: 20,
      listType: "BELL_AREA_UNREAD"
    });
  },

  readNotification: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    notificationsIds: string[]
  ) => {
    const results: ResponseType<IUserNotification[]> = await setNotificationReaded(
      notificationsIds
    );

    if (results.kind === "ok" && results.data) {
      commit("setReadPaginatedNotifications", results.data);
      commit("setReadRecentNotifications", results.data);
      commit("setReadUnreadedRecentNotifications", results.data);

      if (results.data.length !== 1) return;
      const notificationType = results.data[0].type;
      const typeId = results.data[0].relected_type_id;
      if (notificationType === "Archived Process") {
        dispatch("getCompleteProcessFileStream", typeId);
      } else if (
        results.data[0].url_pdf &&
        notificationType === General.TEXTS.KNOWLEDGE_GIVEN
      ) {
        return;
      } else if (isDocumentType(notificationType)) {
        router.push(`/app/documento/${typeId}`);
      } else if (notificationType === "Cancelamento de tramitação automático") {
        const process = results.data[0].relected_type_process;
        await dispatch("readAutoProcessingCancel", { process, typeId });
      } else if (notificationType === "Migração de processo") {
        return router.push("/app/admin/migracoes");
      } else return router.push(`/app/processos/detalhamento/${typeId}`);
    }
  },

  readAutoProcessingCancel: async (
    { commit, dispatch, state }: ActionContext<State, RootState>,
    { process, typeId }: { process: INotificationProcessInfo; typeId: string }
  ) => {
    if (process?.capacity_id !== state.currentUser.capacity?.capacity_id) {
      return showErrorMessage(
        General.MESSAGES.WARNING_AUTOMATIC_PROCESSING_CANCELLED
      );
    }
    if (process?.process_type === "process") {
      await dispatch("editProcessDraft", typeId);
    } else commit("setCurrentTabProcessDetails", "ORDERS");
    return router.push(`/app/processos/detalhamento/${typeId}`);
  },

  readAllNotifications: async (_: ActionContext<State, RootState>) => {
    const results: ResponseGenericType = await setAllNotificationReaded();

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  deleteNotificationRequest: async (
    _: ActionContext<State, RootState>,
    notificationsIds: string[]
  ) => {
    const results: ResponseGenericType = await deleteNotification(
      notificationsIds
    );

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  getTokenDigitalCert: async ({ commit }: ActionContext<State, RootState>) => {
    const results: ResponseType<ITokenGuardian> = await userTokenLoginDigitalCert();

    if (results.kind === "ok") commit("setTokenDigitalCert", results.data);
    else showErrorMessage(results.message);
  },

  loginDigitalCert: async (
    { commit }: ActionContext<State, RootState>,
    hash: string
  ) => {
    const resultsUserToken: ResponseType<IToken> = await userLoginDigitalCert(
      hash
    );

    if (resultsUserToken.kind === "ok") {
      const resultsUserData: ResponseType<IUser> = await getUserData();

      if (resultsUserData.kind === "ok") {
        commit("loginUser", {
          token: resultsUserToken.data,
          userData: resultsUserData.data
        });
        if (resultsUserToken.message)
          showSuccessMessage({ message: resultsUserToken.message });
      } else showErrorMessage(resultsUserData.message);
    } else showErrorMessage(resultsUserToken.message);
  },

  listMessagesAdm: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserMessageRequest = {
      page: 1,
      size: 10,
      start_date: "",
      end_date: "",
      search: ""
    }
  ) => {
    const results: ResponseType<ResponsePaged<
      IListMessage
    >> = await listMessagesAdm(filter);

    if (results?.kind === "ok") {
      commit("setListMessageAdm", results.data);
    } else showErrorMessage(results.message);
  },

  sendMessage: async (
    _: ActionContext<State, RootState>,
    message: IListMessage
  ) => {
    const results: ResponseGenericType = await createMessage(message);

    if (results?.kind === "ok") return results.kind;
    showErrorMessage(results.message);
  },

  deleteMessages: async (
    _: ActionContext<State, RootState>,
    messagesIds: number[]
  ) => {
    const results: ResponseGenericType = await deleteMessage(messagesIds);

    if (results?.kind === "ok") return results.kind;
    showErrorMessage(results.message);
  },

  listMessages: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserMessageRequest = {
      page: 1,
      size: 999
    }
  ) => {
    const results: ResponseType<ResponsePaged<
      IListMessage
    >> = await listMessages(filter);

    if (results?.kind === "ok") {
      commit("setListMessage", results.data);
    } else showErrorMessage(results.message);
  },

  requestUnreadMessagesCount: async (
    { commit }: ActionContext<State, RootState>,
    filter: IUserMessageRequest = {
      page: 1,
      size: 999,
      only_count: true
    }
  ) => {
    const results: ResponseType<IUserMessageOnlyCount> = await listMessages(
      filter
    );

    if (results?.kind === "ok" && results.data) {
      commit("setUnreadMessageCount", results.data.count);
    } else showErrorMessage(results.message);
  },

  readMessage: async (
    { commit }: ActionContext<State, RootState>,
    messageId: string
  ) => {
    const results: ResponseGenericType = await readMessage(messageId);

    if (results?.kind === "ok") {
      commit("updateMessageList", messageId);
    } else showErrorMessage(results.message);
  },

  readAllMessage: async (_: ActionContext<State, RootState>) => {
    const results: ResponseGenericType = await readAllMessage();

    if (results?.kind === "ok") return true;
    showErrorMessage(results.message);
  },

  disableNotificationRequest: async (
    _: ActionContext<State, RootState>,
    hash: string
  ) => {
    const results: ResponseGenericType = await disableNotificationPublic(hash);

    if (results?.kind === "ok") return true;
    showErrorMessage(results.message);
  }
};

const getters = {
  getCurrentUser: (state: State) => state.currentUser,

  getEditableUser: (state: State) => state.editableUser,

  getBookmarks: (state: State) => state.bookmarks,

  getBookmarksUser: (state: State) => state.bookmarksUser,

  getBookmarkAdm: (state: State) => state.bookmarkAdm,

  getUsers: (state: State) => state.usersSearch,

  getUsersFromSearch: (state: State) =>
    state.usersSearch.filter(
      user =>
        !user.bonds?.length || user.bonds.find(bound => bound.suite_access)
    ),

  getUserById: (state: State) => (id: string) =>
    state.usersSearch.find(user => user.id == id),

  hasPermission: (state: State) => (requestedPermission: { id: number }) =>
    state.currentUser.capacity &&
    Boolean(
      state.currentUser.capacity.permissions?.find(
        permission => permission.id === requestedPermission.id
      )
    ),

  hasSomePermission: (state: State) => (permissionNames: string[], count = 1) =>
    state.currentUser.capacity &&
    permissionNames
      .map(name => userPermissions[name as keyof typeof userPermissions])
      .filter(permissionSearch =>
        state.currentUser.capacity?.permissions?.some(
          permission => permission.id === permissionSearch.id
        )
      ).length >= count,

  sessionTime: (state: State) => (currentDate = new Date()) =>
    Number(currentDate) - Number(new Date(state.loginDate)),

  getNotificationConfig: (state: State) => state.notificationConfig,

  getUserNotificationConfig: (state: State) => state.userNotificationConfig,

  getPaginatedNotifications: (state: State) => ({
    results: state.paginatedNotificationsResults,
    count: state.paginatedNotificationsCount
  }),
  getRecentNotifications: (state: State) => ({
    results: state.recentNotificationsResults,
    count: state.recentNotificationsCount
  }),
  getUnreadedRecentNotifications: (state: State) => ({
    results: state.unreadedRecentNotificationsResults,
    count: state.unreadedRecentNotificationsCount
  }),

  isAuthenticated: (state: State) => state.token,

  getUserConfig: (state: State) => state.userConfig,
  getUsertokenDigitalCert: (state: State) => state.tokenDigitalCert,
  getBookmarkFiltered: (state: State) => (filter = "") =>
    Array.isArray(state.bookmarks) &&
    state.bookmarks.filter(bookmark => RegExp(filter, "i").test(bookmark.name)),
  getSignatureBookmarks: (state: State) => (tab: string, filter = "") =>
    state.signatureBookmarks[tab]?.filter(bookmark =>
      RegExp(filter, "i").test(bookmark.name)
    ),
  getFilledKeySignBookmark: (state: State) => state.filledKeySignBookmark,
  getProcessBookmarks: (state: State) => (filter = "") =>
    state.processBookmarks.filter(bookmark =>
      RegExp(filter, "i").test(bookmark.name)
    ),
  getUserSearchFilters: (state: State) => state.userSearchFilters,
  getUserSearchManager: (state: State) => state.userSearchManager,
  isContrast: (state: State) => state.userConfig.theme === "dark",
  isTablet: (state: State) => state.screenWidth && state.screenWidth < 1200,
  isMobile: (state: State) => state.screenWidth && state.screenWidth < 834,
  isSidebarOpenned: (state: State) => state.sidebarOpenned,
  mobileSize: (state: State) => {
    if (state.screenWidth && state.screenWidth <= 320) return sizes.IS_SMALL;
    else if (state.screenWidth && state.screenWidth <= 375)
      return sizes.IS_MEDIUM;
    else return sizes.IS_LARGE;
  },
  getListMessageAdm: (state: State) => state.listMessageAdm,
  getListMessage: (state: State) => state.listMessage,
  getUnreadMessageCount: (state: State) => state.unreadMessageCount
};

export default {
  state,
  mutations,
  actions,
  getters
};
